import { useMemo } from "react";
import { MapContainer, TileLayer, CircleMarker, Popup, Tooltip } from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import { Link } from "react-router-dom";
import "leaflet/dist/leaflet.css";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";

/**
 * CitiesMap — a lightweight world map of every city we have coverage
 * for. Uses Leaflet + free OpenStreetMap tiles (no API key needed).
 *
 * Design intent:
 *   • Circle markers, not the default Leaflet blue pin — gives a
 *     modern, on-brand look and keeps the map legible when zoomed out.
 *   • Cost-tier colouring so a family scanning the map sees at a
 *     glance where the affordable clusters are.
 *   • Hover shows a compact tooltip; click opens a popup with the
 *     student-friendly headline signals + a link to the city page.
 */

const TIER_COLOR = {
  low:    "#059669",  // emerald — cheap
  medium: "#F59E0B",  // amber
  high:   "#DC2626",  // red — expensive
  unknown:"#78716C",  // stone-500
};

function tierFor(city) {
  // The /cities list endpoint exposes cost_of_living.total_monthly (USD-denominated).
  const usd = city?.cost_of_living?.total_monthly
    ?? city?.cost_of_living?.total_monthly_usd;
  if (!usd) return "unknown";
  if (usd < 1200) return "low";
  if (usd < 2200) return "medium";
  return "high";
}

function radiusFor(students) {
  if (!students) return 5;
  // Log-scale so London-scale student pop doesn't drown out smaller cities
  const r = Math.log10(Math.max(students, 1_000)) * 1.6;
  return Math.max(4, Math.min(11, r));
}

export default function CitiesMap({ cities }) {
  // Drop anything without a coord — no fallbacks, we don't want misleading pins.
  const points = useMemo(
    () => (cities || []).filter((c) => typeof c.lat === "number" && typeof c.lng === "number"),
    [cities],
  );

  return (
    <div
      data-testid="cities-map"
      className="rounded-2xl overflow-hidden border border-stone-200 shadow-sm"
      style={{ height: 640 }}
    >
      <MapContainer
        center={[30, 10]}
        zoom={2}
        minZoom={2}
        worldCopyJump
        scrollWheelZoom
        style={{ height: "100%", width: "100%", background: "#F5F0E8" }}
      >
        <TileLayer
          url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://osm.org/copyright">OpenStreetMap</a> contributors'
        />
        <MarkerClusterGroup
          chunkedLoading
          spiderfyOnMaxZoom
          showCoverageOnHover={false}
          maxClusterRadius={45}
        >
        {points.map((c) => {
          const tier = tierFor(c);
          return (
            <CircleMarker
              key={c.slug}
              center={[c.lat, c.lng]}
              radius={radiusFor(c.student_population)}
              pathOptions={{
                color: TIER_COLOR[tier],
                fillColor: TIER_COLOR[tier],
                fillOpacity: 0.7,
                weight: 1.5,
              }}
            >
              <Tooltip direction="top" offset={[0, -6]} opacity={0.95}>
                <div className="font-mono text-[11px]">
                  <div className="font-semibold text-stone-900">{c.name}</div>
                  <div className="text-stone-500">{c.country}</div>
                </div>
              </Tooltip>
              <Popup>
                <div className="min-w-[180px]">
                  <div className="text-[10px] font-underground-sc tracking-wider text-stone-500">
                    {c.country}
                  </div>
                  <div className="font-editorial text-lg text-stone-900 leading-tight">
                    {c.name}
                  </div>
                  {c.student_population && (
                    <div className="mt-1 text-xs text-stone-700">
                      {c.student_population.toLocaleString()} students
                    </div>
                  )}
                  {c.cost_of_living?.total_monthly && (
                    <div className="text-xs text-stone-700">
                      ≈ ${c.cost_of_living.total_monthly.toLocaleString()} / month
                    </div>
                  )}
                  <Link
                    to={`/cities/${c.slug}`}
                    data-testid={`cities-map-link-${c.slug}`}
                    className="mt-2 inline-block text-xs font-underground-sc tracking-wider text-[#4C1D95] hover:underline underline-offset-2"
                  >
                    Open city guide →
                  </Link>
                </div>
              </Popup>
            </CircleMarker>
          );
        })}
        </MarkerClusterGroup>
      </MapContainer>

      <div className="px-4 py-3 bg-white/95 border-t border-stone-200">
        <div className="flex items-center flex-wrap gap-x-5 gap-y-2 text-[11px] font-underground-sc tracking-wider text-stone-600">
          <span className="text-stone-900 font-semibold">Legend:</span>
          <span className="flex items-center gap-1.5" title="Living cost under $1,200 / month (Numbeo total)">
            <span className="inline-block h-3 w-3 rounded-full" style={{ background: TIER_COLOR.low }} />
            Affordable · under $1,200/mo
          </span>
          <span className="flex items-center gap-1.5" title="Living cost $1,200 – $2,200 / month">
            <span className="inline-block h-3 w-3 rounded-full" style={{ background: TIER_COLOR.medium }} />
            Mid · $1,200–$2,200/mo
          </span>
          <span className="flex items-center gap-1.5" title="Living cost over $2,200 / month">
            <span className="inline-block h-3 w-3 rounded-full" style={{ background: TIER_COLOR.high }} />
            Expensive · over $2,200/mo
          </span>
          <span className="flex items-center gap-1.5">
            <span className="inline-block h-3 w-3 rounded-full" style={{ background: TIER_COLOR.unknown }} />
            No cost data
          </span>
          <span className="text-stone-400 normal-case tracking-normal">
            · Pin size grows with student population · Zoom in to expand clusters
          </span>
          <span className="ml-auto text-stone-400">{points.length} cities plotted</span>
        </div>
      </div>
    </div>
  );
}
